import { useState, useLayoutEffect } from "react";
import { Sparkles, X } from "lucide-react";

const STEPS = [
  { target: "add-card-button", title: "Add anything ✦", text: "Tasks, notes, chats, WhatsApp, news, motivation — build your whole day from this one button.", pos: "below" },
  { target: "canvas-area", title: "Your freeform canvas", text: "Drag cards anywhere, pan the empty space, resize from the corner and link cards with arrows.", pos: "center" },
  { target: "view-toggle", title: "Grid view", text: "One tap tidies everything into a neat bento grid. Tap again to go back freeform.", pos: "below" },
  { target: "vibe-button", title: "Set the vibe", text: "12 artboard moods — Happy, Excited, Sad, Heartbroken. Restyle the whole board in one tap.", pos: "above" },
];

export default function WelcomeTour({ onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = STEPS[i];

  useLayoutEffect(() => {
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [step.target]);

  const next = () => (i < STEPS.length - 1 ? setI(i + 1) : onClose());

  const tipStyle = (() => {
    if (!rect || step.pos === "center") return { left: "50%", top: "50%", transform: "translate(-50%,-50%)" };
    const left = Math.min(window.innerWidth - 300, Math.max(12, rect.left + rect.width / 2 - 144));
    if (step.pos === "below") return { left, top: rect.bottom + 14 };
    return { left, top: Math.max(12, rect.top - 172) };
  })();

  return (
    <div className="fixed inset-0 z-[100]" data-testid="welcome-tour">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" onClick={onClose} />
      {rect && step.pos !== "center" && (
        <div className="pointer-events-none absolute rounded-2xl ring-4 ring-violet-400 transition-all duration-300"
          style={{ left: rect.left - 6, top: rect.top - 6, width: rect.width + 12, height: rect.height + 12 }} />
      )}
      <div className="absolute w-72 rounded-2xl border border-violet-400/40 bg-card p-4 text-foreground shadow-2xl card-pop"
        style={tipStyle} data-testid="tour-tooltip">
        <div className="flex items-center gap-1.5 font-display font-bold">
          <Sparkles className="h-4 w-4 text-violet-500" /> {step.title}
          <button data-testid="tour-skip" onClick={onClose} className="ml-auto rounded p-0.5 opacity-60 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1.5 text-sm text-muted-foreground">{step.text}</p>
        <div className="mt-3 flex items-center">
          <div className="flex gap-1">
            {STEPS.map((_, d) => (
              <span key={d} className={`h-1.5 w-1.5 rounded-full ${d === i ? "bg-violet-500" : "bg-muted-foreground/30"}`} />
            ))}
          </div>
          <button data-testid="tour-next" onClick={next}
            className="ml-auto rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 px-4 py-1.5 text-sm font-bold text-white transition-transform hover:scale-105 active:scale-95">
            {i === STEPS.length - 1 ? "Let's go ✦" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}
